import type { ApiBody } from '@hello-aria/makalu-api-client'
import { isEmpty, isNil, uniqueId } from 'lodash'
import { z } from 'zod'

import type { BillingDocumentType } from './loanModels'
import type { Root } from './rootModel'

export const proofTypes = [
    'INVOICE',
    'CMR',
    'PURCHASE_ORDER',
    'PROFORMA_INVOICE',
    'TRANSPORT_ORDER',
    'DELIVERY_NOTE',
    'TIMESHEET',
    'INVOICE_VALIDATION',
    'INVOICE_DELIVERY',
    'TRANSACTION_PROOF',
    'PAYMENT_PROOF',
    'CREDIT_MEMO',
    'DISBURSEMENT_NOTE',
    'ADDITIONAL_INVOICE',
    'INVOICE_BREAKDOWN',
    'SUPPLIER_CONTRACT',
    'OTHER',
    'UNKNOWN'
] as const

export type ProofType = (typeof proofTypes)[number]

export const proofSources = ['COMPANY', 'ARIA'] as const

export type ProofSource = (typeof proofSources)[number]

export const proofTargetTypes = ['Invoice', 'Receivable', 'CreditMemo', 'Loan'] as const

export type ProofTargetType = (typeof proofTargetTypes)[number]

export type ProofTarget = {
    type: ProofTargetType
    id: string
}

export type ProofGateway = Root & {
    type: ProofType
    source: ProofSource
    label: string | null
    fileId: string | null
    legacyUrl: string | null
    targets: ProofTarget[]
    archivedAt: string | null
}

export type Proof = ProofGateway

export type ProofCreationGateway = ApiBody<'/companies/{companyId}/proofs', 'post'>

/**
 * The types a supplier can give a document it uploads, per target, in the order the select lists them.
 * The exact set of `anyTargetProofTypes` of the backend (packages/business/src/proof/index.ts), excluding UNKNOWN.
 */
export const proofTypesByTargetType = {
    Invoice: [
        'INVOICE',
        'DISBURSEMENT_NOTE',
        'ADDITIONAL_INVOICE',
        'INVOICE_BREAKDOWN',
        'CMR',
        'TRANSPORT_ORDER',
        'DELIVERY_NOTE',
        'TIMESHEET',
        'INVOICE_VALIDATION',
        'INVOICE_DELIVERY',
        'SUPPLIER_CONTRACT',
        'OTHER'
    ],
    Receivable: [
        'PURCHASE_ORDER',
        'PROFORMA_INVOICE',
        'TRANSACTION_PROOF',
        'PAYMENT_PROOF',
        'SUPPLIER_CONTRACT',
        'OTHER'
    ],
    CreditMemo: ['CREDIT_MEMO', 'OTHER'],
    Loan: ['CMR', 'ADDITIONAL_INVOICE', 'INVOICE_BREAKDOWN', 'TIMESHEET', 'OTHER']
} as const satisfies Record<ProofTargetType, readonly ProofType[]>

/** A file picked for upload, before it becomes a proof. Its type stays null until the supplier picks one. */
export type ProofDraft = {
    id: string
    file: File
    type: ProofType | null
    label: string
}

/** A new draft of the file, untyped until the supplier picks a type. */
export const toProofDraft = (file: File): ProofDraft => ({ id: uniqueId('proof-draft-'), file, type: null, label: '' })

export const isProofLabelRequired = (type: ProofType | null): boolean => type === 'OTHER'

const isLabelMissing = ({ type, label }: Pick<ProofDraft, 'type' | 'label'>): boolean =>
    isProofLabelRequired(type) && isEmpty(label.trim())

/** The messages of the proof schemas are keys of the `proofs` namespace, which the upload field translates. */
export const proofUploadErrors = {
    typeRequired: 'upload.errors.typeRequired',
    labelRequired: 'upload.errors.labelRequired',
    untyped: 'upload.errors.untyped',
    empty: 'upload.errors.empty',
    invoiceRequired: 'upload.errors.invoiceRequired'
} as const

export const proofDraftSchema = z
    .object({
        id: z.string(),
        file: z.instanceof(File),
        type: z.enum(proofTypes, { message: proofUploadErrors.typeRequired }),
        label: z.string()
    })
    .superRefine((draft, ctx) => {
        if (isLabelMissing(draft)) {
            ctx.addIssue({ code: 'custom', path: ['label'], message: proofUploadErrors.labelRequired })
        }
    })

export type ValidProofDraft = z.infer<typeof proofDraftSchema>

export const hasInvoiceOrDisbursementNote = (drafts: Pick<ProofDraft, 'type'>[]): boolean =>
    drafts.some(({ type }) => type === 'INVOICE' || type === 'DISBURSEMENT_NOTE')

export const countUntypedDrafts = (drafts: Pick<ProofDraft, 'type'>[]): number =>
    drafts.filter(({ type }) => isNil(type)).length

const proofDraftInputSchema = z.object({
    id: z.string(),
    file: z.instanceof(File),
    type: z.enum(proofTypes).nullable(),
    label: z.string()
})

const proofDraftListSchema = z.array(proofDraftInputSchema)

/** The documents a form field holds, or none when the field holds anything else. */
export const toProofDrafts = (value: unknown): ProofDraft[] => {
    const parsed = proofDraftListSchema.safeParse(value)
    return parsed.success ? parsed.data : []
}

type ProofDraftsSchemaOptions = {
    /** The target needs at least one document. A loan, for one, is never gated on its documents. */
    requiresOneDocument?: boolean
    /** The target needs an invoice or a disbursement note among its documents. */
    requiresInvoiceOrDisbursementNote?: boolean
}

/**
 * The documents of one upload field. The list carries one message for the whole field, and a row
 * carries the message of its missing label.
 */
export const toProofDraftsSchema = ({
    requiresOneDocument = true,
    requiresInvoiceOrDisbursementNote = false
}: ProofDraftsSchemaOptions = {}) =>
    proofDraftListSchema
        .superRefine((drafts, ctx) => {
            if (isEmpty(drafts)) {
                if (requiresOneDocument) {
                    ctx.addIssue({ code: 'custom', message: proofUploadErrors.empty })
                }
            } else if (countUntypedDrafts(drafts) > 0) {
                ctx.addIssue({ code: 'custom', message: proofUploadErrors.untyped })
            } else if (requiresInvoiceOrDisbursementNote && !hasInvoiceOrDisbursementNote(drafts)) {
                ctx.addIssue({ code: 'custom', message: proofUploadErrors.invoiceRequired })
            }

            drafts.forEach((draft, index) => {
                if (isLabelMissing(draft)) {
                    ctx.addIssue({ code: 'custom', path: [index, 'label'], message: proofUploadErrors.labelRequired })
                }
            })
        })
        .pipe(z.array(proofDraftSchema))

/** The type and the label a supplier gave a file, carried from one step of the loan creation to the next. */
export type ProofTypeChoice = Pick<ValidProofDraft, 'type' | 'label'>

const proofTypeChoicesSchema = z.array(z.object({ type: z.enum(proofTypes), label: z.string() }))

/** One draft per file. A file typed on the previous step keeps its type and label, any other takes `fallback`, or waits for the supplier to type it. */
export const toInitialProofDrafts = (files: File[], proofTypeChoices: ProofTypeChoice[], fallback?: ProofTypeChoice) =>
    files.map((file, index) => ({ ...toProofDraft(file), ...(proofTypeChoices[index] ?? fallback) }))

/** The `proofTypes` param of the loan creation steps: one choice per file, in the order of the `fileIds` param. */
export const toProofTypesParam = (drafts: ProofTypeChoice[]): string =>
    encodeURIComponent(JSON.stringify(drafts.map(({ type, label }) => ({ type, label }))))

/** The choices a `proofTypes` param carries, or none when it carries anything else. */
export const parseProofTypesParam = (param: string): ProofTypeChoice[] => {
    try {
        const parsed = proofTypeChoicesSchema.safeParse(JSON.parse(decodeURIComponent(param)))
        return parsed.success ? parsed.data : []
    } catch {
        return []
    }
}

const isProofTypeOf = (targetType: ProofTargetType, type: ProofType): boolean => {
    const acceptedTypes: readonly ProofType[] = proofTypesByTargetType[targetType]
    return acceptedTypes.includes(type)
}

/** The proof target of the documents a supplier uploads for a billing document. */
export const proofTargetTypeOf = (billingDocumentType: BillingDocumentType): ProofTargetType =>
    billingDocumentType === 'receivable' ? 'Receivable' : 'Invoice'

/** The drafts, keeping their files, with the types the target refuses cleared so the supplier picks again. */
export const withoutRefusedProofTypes = (targetType: ProofTargetType, drafts: ProofDraft[]): ProofDraft[] =>
    drafts.map((draft) =>
        isNil(draft.type) || isProofTypeOf(targetType, draft.type) ? draft : { ...draft, type: null }
    )

type ProofUploadAccess = {
    hasProofs: boolean
    hasProofUpload: boolean
    isEditable: boolean
}

export const canAddProofsTo = ({ hasProofs, hasProofUpload, isEditable }: ProofUploadAccess): boolean =>
    hasProofs && hasProofUpload && isEditable

export const toProofCreationBody = (
    fileId: string,
    { type, label }: Pick<ValidProofDraft, 'type' | 'label'>,
    targets?: ProofTarget[]
): ProofCreationGateway => ({
    fileId,
    type,
    // The API reads a label as the description of the document, so a blank one is not sent.
    ...(isEmpty(label.trim()) ? {} : { label: label.trim() }),
    ...(isNil(targets) ? {} : { targets })
})
